/**
 * src/api/aiClient.js - Dispatcher AI assistant client
 *
 * The browser talks ONLY to the Call It In TMS backend (POST /ai/ask), using
 * the same authenticated request() helper as every other API module. The
 * backend (admin/dispatcher only) forwards the prompt to the private,
 * token-protected inference gateway; no model URL, gateway URL, or
 * inference credential is ever present in the browser.
 *
 * Contract
 *   POST /ai/ask   { prompt: string, context?: string, tms_data?: boolean, tz?: string }
 *   200            { status: "ok", completion: string }
 *   401 / 403      not signed in / not admin or dispatcher
 *   422            prompt empty or too long
 *   429            rate limited
 *   502 / 503      AI gateway unreachable / not configured
 */

import { request } from "./client";

export const AI_PROMPT_MAX_CHARS = 2000;
// Must not exceed the backend's MAX_CONTEXT_CHARS (ai_gateway.py). The Admin AI
// page sends a ~3000-character live snapshot; the TMS adds its own database
// extract server-side, so the browser no longer has to carry history.
export const AI_CONTEXT_MAX_CHARS = 8000;

async function _errorMessage(res) {
  let detail = "";
  try {
    const data = await res.json();
    detail = typeof data?.detail === "string" ? data.detail : "";
  } catch {
    // non-JSON error body
  }
  if (res.status === 403) return "AI assistant is available to admin and dispatcher accounts only.";
  if (res.status === 429) return "Too many AI requests. Please wait a minute and try again.";
  if (res.status === 503) return detail || "The AI assistant is not configured on the server.";
  if (res.status === 502) return detail || "The AI assistant is temporarily unavailable.";
  return detail || `AI request failed (HTTP ${res.status}).`;
}

/**
 * Ask the AI assistant a question.
 * @param {string} prompt
 * @param {string} [context] optional operational context (driver / task summary)
 * @param {object} [options]
 * @param {boolean} [options.tmsData] ask the TMS backend to retrieve a read-only,
 *   role-scoped extract of its own database for this question (history, invoices,
 *   customers, drivers, POD, address book). The backend decides what is read; the
 *   browser sends only the question, its time zone (so "September" means the
 *   viewer's September) and an optional live snapshot.
 * @returns {Promise<string>} the completion text
 */
export async function askAI(prompt, context = "", { tmsData = false } = {}) {
  let timeZone;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    timeZone = undefined;
  }
  const res = await request("/ai/ask", {
    method: "POST",
    body: {
      prompt: String(prompt || "").slice(0, AI_PROMPT_MAX_CHARS),
      ...(context ? { context: String(context).slice(0, AI_CONTEXT_MAX_CHARS) } : {}),
      ...(tmsData ? { tms_data: true, ...(timeZone ? { tz: timeZone } : {}) } : {}),
    },
    authContext: { message: "Your session expired while contacting the AI assistant." },
  });
  if (!res.ok) throw new Error(await _errorMessage(res));
  const data = await res.json();
  return typeof data?.completion === "string" ? data.completion : "";
}

/**
 * Parse a model reply into [{ title, detail }] suggestions. Accepts a JSON
 * array (optionally fenced) and falls back to one card holding the raw text,
 * so a chatty or non-JSON reply still renders instead of failing silently.
 */
export function parseSuggestions(text) {
  const raw = String(text || "").trim();
  if (!raw) return [];
  const unfenced = raw.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "");
  const start = unfenced.indexOf("[");
  const end = unfenced.lastIndexOf("]");
  if (start !== -1 && end > start) {
    try {
      const arr = JSON.parse(unfenced.slice(start, end + 1));
      if (Array.isArray(arr)) {
        const items = arr
          .map((s) => ({
            title: String(s?.title ?? "").trim(),
            detail: String(s?.detail ?? "").trim(),
          }))
          .filter((s) => s.title || s.detail)
          .slice(0, 5);
        if (items.length) return items;
      }
    } catch {
      // fall through to plain-text card
    }
  }
  return [{ title: "AI suggestion", detail: raw }];
}

/**
 * Ask for up to 3 dispatcher suggestions for a fleet snapshot.
 * @param {string} fleetSummary plain-text summary, one driver per line
 * @returns {Promise<Array<{title:string, detail:string}>>}
 */
export async function getDispatchSuggestions(fleetSummary) {
  const prompt =
    "You are a dispatch assistant for a medical courier company. Based on the " +
    "driver statuses below, give up to 3 short, actionable suggestions for the " +
    'dispatcher. Reply ONLY with a JSON array of objects with "title" and ' +
    '"detail" string fields.';
  const text = await askAI(prompt, fleetSummary);
  return parseSuggestions(text);
}
